import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import api from "@/lib/api";
import { ScoreBar } from "@/components/Primitives";

export default function Learn() {
  const [modules, setModules] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [access, setAccess] = useState({ full: true, moduleLimit: null });

  useEffect(() => {
    Promise.all([api.get("/modules"), api.get("/lessons")])
      .then(([m, l]) => {
        setModules(m.data.modules);
        setLessons(l.data.lessons);
        if (l.data.access) setAccess(l.data.access);
      })
      .catch(() => setModules([]));
  }, []);

  if (!modules)
    return <div className="grid place-items-center py-24" data-testid="learn-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8 fade-up" data-testid="learn-page">
      <div>
        <p className="overline text-primary">learn</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">Curriculum</h1>
        <p className="mt-3 text-sm text-muted-foreground">{modules.length} modules · {lessons.length} lessons · written for production, not for slides.</p>
        {!access.full && (
          <p className="mt-3 font-mono text-xs text-destructive" data-testid="learn-locked-notice">
            Free plan: modules 1–{access.moduleLimit} only. <Link to="/pricing" className="underline">Upgrade</Link> to unlock the full curriculum.
          </p>
        )}
      </div>

      <div className="border-t border-l border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {modules.map((m) => {
          const ml = lessons.filter((l) => l.moduleSlug === m.slug);
          const done = ml.filter((l) => l.completed).length;
          const pct = ml.length ? Math.round((done / ml.length) * 100) : 0;
          return (
            <div key={m.slug} data-testid={`learn-module-${m.slug}`} className="border-r border-b border-border p-6 hover:bg-accent/40 transition-colors duration-150">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-xs text-primary">{String(m.order).padStart(2, "0")}</span>
                <span className="font-mono text-xs text-muted-foreground">{m.estimatedHours}h</span>
              </div>
              <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{m.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{m.summary}</p>
              <div className="mt-4"><ScoreBar value={pct} /></div>
              <p className="mt-2 font-mono text-xs text-muted-foreground">{done}/{ml.length} lessons</p>
              <div className="mt-4 space-y-1">
                {ml.map((l) =>
                  l.locked ? (
                    <Link key={l.slug} to="/pricing" data-testid={`learn-locked-${l.slug}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors duration-150">
                      <Lock className="h-3 w-3" /> {l.title}
                    </Link>
                  ) : (
                    <Link key={l.slug} to={`/learn/${l.slug}`} data-testid={`learn-lesson-${l.slug}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-150">
                      <ArrowRight className="h-3 w-3" /> {l.title}
                    </Link>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
